import { Component, HostBinding, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { delay, take } from 'rxjs/operators';
import {
    DeliveryDefenseScoreInformationDialogComponent,
} from '../../../ship/components/delivery-defense/delivery-defense-score-information-dialog/delivery-defense-score-information-dialog.component';

@Component({
    selector: 'upsc-circular-confidence-gauge',
    templateUrl: './circular-confidence-gauge.component.html',
    styleUrls: ['./circular-confidence-gauge.component.scss'],
})
export class CircularConfidenceGaugeComponent implements OnInit, OnChanges {
    @Input() public value = 0;
    @Input() public min = 100;
    @Input() public max = 1000;
    @Input() public coverageThreshold = 499;
    @Input() public gradientColors = ['#f66e6e', '#f6ac68', '#f1d367', '#e1f46d', '#97f26c'];
    @Input() public backgroundColor = '#fff';
    @Input() public thickness = '1.25rem';

    @HostBinding('class') public hostClass = 'circular-confidence-gauge';
    public showCoverageResult$ = new BehaviorSubject<boolean>(false);
    public gradient: string;
    public progressPercentage$ = new BehaviorSubject<number>(0);
    // Make a conic gradient starting from 225deg up to -45deg (which make it 270deg in total).
    public conicFromDegree = 225;
    public conicToDegree = -45;
    private conicTotalDegree = this.conicFromDegree - this.conicToDegree;

    constructor(private readonly dialog: MatDialog) {
    }

    public get progressPercentage(): number {
        this.showCoverageResult$.next(false);

        let valuePercentage: number;
        switch (true) {
            case (this.value <= this.min):
                valuePercentage = 0;
                break;
            case (this.value >= this.max):
                valuePercentage = 100;
                break;
            default:
                valuePercentage = (this.value - this.min) * 100 / (this.max - this.min);
                break;
        }

        this.showCoverageResult$.next(true);
        return valuePercentage;
    }

    public ngOnInit() {
        this.setGaugeGradient();
        this.animateMarker();
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes?.value?.currentValue === null || changes?.value?.currentValue === undefined) {
            this.value = 0;
        }
    }

    public openScoreInfoDialog(event: MouseEvent) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        const dialogConfig: MatDialogConfig = {
            disableClose: true,
            width: '70%',
            panelClass: 'mobile-fullscreen-dialog',
            data: {},
        };

        const dialogRef = this.dialog.open(DeliveryDefenseScoreInformationDialogComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(() => {
        });
    }

    private animateMarker(): void {
        /**
         * This `progressPercentage$` is a BehaviorSubject.
         * It re-emits the last value to any new subscriber.
         */
        this.progressPercentage$
            .pipe(
                /**
                 *  This causes the observable to wait for 10 milliseconds before emitting the value,
                 *  often used to ensure that certain asynchronous operations or UI updates have a chance to complete before the next step.
                 *
                 * WARN: Do not set delay to 0 without using `take(1)` to unsubscribe it.
                 *  Otherwise, it will create an infinite loop:
                 *  1. progressPercentage$.pipe(delay(0)) subscribes immediately.
                 *  2. `this.progressPercentage$.next(this.progressPercentage);` called in that subscription immediately emits to any subscription.
                 *  3. This consequently triggers the subscription again.
                 *  --> infinite loop!
                 */
                delay(10),

                /**
                 * The intention of this observable is to only set the progress once.
                 * This will unsubscribe it after the first emit.
                 * Otherwise, it will create an infinite loop since every emission leads to another immediate emission
                 * and there is no condition to break the cycle.
                 */
                take(1),
            )
            .subscribe(() => {
                this.progressPercentage$.next(this.progressPercentage);
            });
    }

    private setGaugeGradient(): void {
        // Separate each color in the list evenly.
        const colorPortionPercentage = 100 / (this.gradientColors.length - 1);

        // Set gradient percentage for each color.
        let gradientColorsWithPositions =
            this.gradientColors
                .filter(Boolean)
                .map((color, index) => {
                    return `${ color } ${ index * colorPortionPercentage * this.conicTotalDegree / 360 }%`;
                }).join(', ');

        // Fill the remaining space of the circle with transparent color.
        const lastPercentage = (this.gradientColors.length - 1) * colorPortionPercentage * this.conicTotalDegree / 360;
        gradientColorsWithPositions += `, transparent ${ lastPercentage }%, transparent 100%`;
        this.gradient = `conic-gradient(from ${ this.conicFromDegree }deg, ${ gradientColorsWithPositions })`;
    }
}
